import type { DateTimeOptions } from 'luxon';
import { DateTime, Info } from 'luxon';
import type { Tagged } from 'type-fest';

import LoggingService from 'components/core/logging/LoggingService';
import { DateTimeFormat } from 'enums/dateTimeFormat';

import { localeStorage } from './storage/intl';

const {
  DATE_TIME_FORMAT,
  DATE_YEAR_FORMAT,
  DAY_DATE_YEAR_FORMAT,
  DEFAULT_TIME_ZONE,
  TIME_FORMAT,
  YEAR_MONTH_DAY_SLASH_FORMAT,
} = DateTimeFormat;

export const getCurrentLocalDateTime = () => DateTime.local();

const isJSDate = (jsDate: Date | string): jsDate is Date => Object.prototype.toString.call(jsDate) === '[object Date]';

/**
 * Narrows a DateTime of unknown validity, which is what luxon's factory methods return.
 * Their `isValid` is a plain boolean, so a bare truthiness check cannot narrow on its own.
 */
const isValidDateTime = (dateTime: DateTime<boolean>): dateTime is DateTime<true> => dateTime.isValid;

/**
 * A calendar date in the API's `Date` scalar format, 'yyyy-MM-dd'.
 * The brand keeps it apart from date-time strings, which the API rejects where it expects a date.
 */
export type LocalDate = Tagged<string, 'LocalDate'>;

/**
 * Returns the calendar date in the date-time's own zone, not in UTC.
 */
export const toLocalDate = (dateTime: DateTime<true>): LocalDate => dateTime.toISODate() as LocalDate;

/**
 * Parses a wire value as a local date.
 * Returns null for anything other than a real calendar date written as 'yyyy-MM-dd'.
 */
export const parseLocalDate = (value: string): LocalDate | null => {
  const dateTime = DateTime.fromFormat(value, 'yyyy-MM-dd');

  return isValidDateTime(dateTime) ? toLocalDate(dateTime) : null;
};

/**
 * Gets the current time zone of user.
 * Time zone needs to be a valid IANA zone otherwise defaults to UTC.
 */
export const getTimeZone = (date?: any, defaultTimeZone = DEFAULT_TIME_ZONE) => {
  const currentDateTime = date || getCurrentLocalDateTime();
  const zoneName = currentDateTime.zoneName;

  if (Info.isValidIANAZone(zoneName)) {
    return zoneName;
  }
  LoggingService.warn({
    message: `DateUtils.ts: Error retrieving current timeZone, defaulting to: ${defaultTimeZone}`,
  });

  return defaultTimeZone;
};

export function getDateTime(): DateTime<true>;
export function getDateTime(
  date: null | string | undefined,
  fromFormat?: string,
  options?: DateTimeOptions
): DateTime<true> | null;
export function getDateTime(date: Date | null): DateTime<true> | null;
export function getDateTime(date?: Date | null | string, fromFormat?: string, options?: DateTimeOptions) {
  if (!date) {
    return getCurrentLocalDateTime();
  }

  let dateTime: DateTime<boolean>;
  if (typeof date === 'string' && fromFormat) {
    dateTime = DateTime.fromFormat(date, fromFormat, options);
  } else {
    dateTime = isJSDate(date) ? DateTime.fromJSDate(date) : DateTime.fromISO(date, options);
  }

  return isValidDateTime(dateTime) ? dateTime : null;
}

export const getJSDate = (date?: string) => {
  const dateTime = date ? DateTime.fromISO(date) : getCurrentLocalDateTime();

  return dateTime.isValid ? dateTime.toJSDate() : null;
};

export const isToday = (date: string): boolean => {
  const currentDateTime = getDateTime(date);

  return !!currentDateTime && getDateTime().hasSame(currentDateTime, 'day', {});
};

export const getFormattedDateTimeString = (
  date: DateTime<true> | null | string,
  format?: DateTimeFormat,
  isLong?: boolean
) => {
  const selectedDate = typeof date === 'string' ? getDateTime(date) : date;

  let formattedDateTimeString: string | undefined;
  switch (format) {
    case DATE_TIME_FORMAT: {
      formattedDateTimeString = selectedDate?.toFormat(`LLL${isLong ? 'L' : ''} dd yyyy, h:mm a`);
      break;
    }

    case DAY_DATE_YEAR_FORMAT: {
      formattedDateTimeString = selectedDate?.toFormat(`EEE${isLong ? 'E' : ''}, LLL dd, yyyy`);
      break;
    }

    case null:
    case undefined: {
      // The time zone is always specified in the Time-Zone header.
      formattedDateTimeString = selectedDate?.toISO({ includeOffset: false });
      break;
    }

    default: {
      formattedDateTimeString = selectedDate?.toFormat(format);
      break;
    }
  }

  return formattedDateTimeString;
};

/**
 * Returns 'yesterday'/'today'/'tomorrow' if date is recent, null otherwise
 * @param date an ISO date string
 */
export const getRecentDateName = (date: string): null | string => {
  const unit = 'days';
  const dateTime = getDateTime(date);
  const today = getDateTime();
  const tomorrow = today.plus({ days: 1 });
  const yesterday = today.minus({ days: 1 });

  const isRecent = [today, tomorrow, yesterday].some(day => dateTime?.hasSame(day, 'day'));

  return isRecent && dateTime ? dateTime?.setLocale(localeStorage.get() || '')?.toRelativeCalendar({ unit }) : null;
};

/**
 * Returns XX:XX PM/AM if date is today, 'Yesterday' or 'YYYY/MM/DD' if it's not, null otherwise
 * @param date an ISO date string
 */
export const getRecentDateTime = (date: string): null | string => {
  const dateTime = getDateTime(date);

  if (isToday(date)) {
    return dateTime?.toLocaleString(DateTime.TIME_SIMPLE) || null;
  }
  if (dateTime?.hasSame(getDateTime().minus({ days: 1 }), 'day')) {
    return getRecentDateName(date);
  }

  return getFormattedDateTimeString(date, YEAR_MONTH_DAY_SLASH_FORMAT) || null;
};

/**
 * Gets a detailed string representation of a DateTime relative to now
 * e.g. today -> Today - 4:20 PM
 *      dec 25th -> Dec 25, 2021 - 4:20 PM
 *
 * @param date an ISO date string
 */
export const getRelativeDateFormat = (date: string): string => {
  const day = getRecentDateName(date) || getFormattedDateTimeString(date, DATE_YEAR_FORMAT);
  const time = getFormattedDateTimeString(date, TIME_FORMAT);

  return `${day} - ${time}`;
};
